import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  sendEmailVerification,
  OAuthProvider,
  linkWithCredential,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase.js";
import { CheckCircle2, Flag, Globe, Trophy } from "lucide-react";
import exquraWordmark from "../assets/branding/exqura-wordmark.png";
import { getFunctions, httpsCallable } from "firebase/functions";

// ─── Icons ────────────────────────────────────────────────────────────────────

const GoogleIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z"
      fill="#4285F4"
    />
    <path
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z"
      fill="#34A853"
    />
    <path
      d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z"
      fill="#EA4335"
    />
  </svg>
);

const MailCheckIcon = () => (
  <svg
    width="48"
    height="48"
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="48" height="48" rx="24" fill="rgba(212,175,55,0.15)" />
    <path
      d="M10 16a2 2 0 0 1 2-2h24a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H12a2 2 0 0 1-2-2V16Z"
      stroke="#D4AF37"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="m10 16 14 10 14-10"
      stroke="#D4AF37"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="34" cy="34" r="7" fill="#D4AF37" />
    <path
      d="m31 34 2 2 4-4"
      stroke="#0B2421"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SignpassButton = () => (
  <svg
    width="198"
    height="40"
    viewBox="0 0 198 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="198" height="40" rx="6" fill="#D93841" />
    <path
      d="M12.462 20.072C12.462 19.1013 12.672 18.238 13.092 17.482C13.512 16.7167 14.0953 16.124 14.842 15.704C15.598 15.2747 16.452 15.06 17.404 15.06C18.5707 15.06 19.5693 15.368 20.4 15.984C21.2307 16.6 21.786 17.44 22.066 18.504H19.434C19.238 18.0933 18.958 17.7807 18.594 17.566C18.2393 17.3513 17.8333 17.244 17.376 17.244C16.6387 17.244 16.0413 17.5007 15.584 18.014C15.1267 18.5273 14.898 19.2133 14.898 20.072C14.898 20.9307 15.1267 21.6167 15.584 22.13C16.0413 22.6433 16.6387 22.9 17.376 22.9C17.8333 22.9 18.2393 22.7927 18.594 22.578C18.958 22.3633 19.238 22.0507 19.434 21.64H22.066C21.786 22.704 21.2307 23.544 20.4 24.16C19.5693 24.7667 18.5707 25.07 17.404 25.07C16.452 25.07 15.598 24.86 14.842 24.44C14.0953 24.0107 13.512 23.418 13.092 22.662C12.672 21.906 12.462 21.0427 12.462 20.072ZM27.0881 25.112C26.3227 25.112 25.6321 24.9487 25.0161 24.622C24.4094 24.2953 23.9287 23.8287 23.5741 23.222C23.2287 22.6153 23.0561 21.906 23.0561 21.094C23.0561 20.2913 23.2334 19.5867 23.5881 18.98C23.9427 18.364 24.4281 17.8927 25.0441 17.566C25.6601 17.2393 26.3507 17.076 27.1161 17.076C27.8814 17.076 28.5721 17.2393 29.1881 17.566C29.8041 17.8927 30.2894 18.364 30.6441 18.98C30.9987 19.5867 31.1761 20.2913 31.1761 21.094C31.1761 21.8967 30.9941 22.606 30.6301 23.222C30.2754 23.8287 29.7854 24.2953 29.1601 24.622C28.5441 24.9487 27.8534 25.112 27.0881 25.112ZM27.0881 23.04C27.5454 23.04 27.9327 22.872 28.2501 22.536C28.5767 22.2 28.7401 21.7193 28.7401 21.094C28.7401 20.4687 28.5814 19.988 28.2641 19.652C27.9561 19.316 27.5734 19.148 27.1161 19.148C26.6494 19.148 26.2621 19.316 25.9541 19.652C25.6461 19.9787 25.4921 20.4593 25.4921 21.094C25.4921 21.7193 25.6414 22.2 25.9401 22.536C26.2481 22.872 26.6307 23.04 27.0881 23.04ZM37.2061 17.104C38.1208 17.104 38.8488 17.4027 39.3901 18C39.9408 18.588 40.2161 19.4 40.2161 20.436V25H37.8361V20.758C37.8361 20.2353 37.7008 19.8293 37.4301 19.54C37.1595 19.2507 36.7955 19.106 36.3381 19.106C35.8808 19.106 35.5168 19.2507 35.2461 19.54C34.9755 19.8293 34.8401 20.2353 34.8401 20.758V25H32.4461V17.188H34.8401V18.224C35.0828 17.8787 35.4095 17.608 35.8201 17.412C36.2308 17.2067 36.6928 17.104 37.2061 17.104ZM46.2337 22.97V25H45.0157C44.1477 25 43.4711 24.79 42.9857 24.37C42.5004 23.9407 42.2577 23.2453 42.2577 22.284V19.176H41.3057V17.188H42.2577V15.284H44.6517V17.188H46.2197V19.176H44.6517V22.312C44.6517 22.5453 44.7077 22.7133 44.8197 22.816C44.9317 22.9187 45.1184 22.97 45.3797 22.97H46.2337ZM48.7712 16.376C48.3512 16.376 48.0059 16.2547 47.7352 16.012C47.4739 15.76 47.3432 15.452 47.3432 15.088C47.3432 14.7147 47.4739 14.4067 47.7352 14.164C48.0059 13.912 48.3512 13.786 48.7712 13.786C49.1819 13.786 49.5179 13.912 49.7792 14.164C50.0499 14.4067 50.1852 14.7147 50.1852 15.088C50.1852 15.452 50.0499 15.76 49.7792 16.012C49.5179 16.2547 49.1819 16.376 48.7712 16.376ZM49.9612 17.188V25H47.5672V17.188H49.9612ZM56.4561 17.104C57.3708 17.104 58.0988 17.4027 58.6401 18C59.1908 18.588 59.4661 19.4 59.4661 20.436V25H57.0861V20.758C57.0861 20.2353 56.9508 19.8293 56.6801 19.54C56.4095 19.2507 56.0455 19.106 55.5881 19.106C55.1308 19.106 54.7668 19.2507 54.4961 19.54C54.2255 19.8293 54.0901 20.2353 54.0901 20.758V25H51.6961V17.188H54.0901V18.224C54.3328 17.8787 54.6595 17.608 55.0701 17.412C55.4808 17.2067 55.9428 17.104 56.4561 17.104ZM68.8437 17.188V25H66.4497V23.936C66.2071 24.2813 65.8757 24.5613 65.4557 24.776C65.0451 24.9813 64.5877 25.084 64.0837 25.084C63.4864 25.084 62.9591 24.9533 62.5017 24.692C62.0444 24.4213 61.6897 24.034 61.4377 23.53C61.1857 23.026 61.0597 22.4333 61.0597 21.752V17.188H63.4397V21.43C63.4397 21.9527 63.5751 22.3587 63.8457 22.648C64.1164 22.9373 64.4804 23.082 64.9377 23.082C65.4044 23.082 65.7731 22.9373 66.0437 22.648C66.3144 22.3587 66.4497 21.9527 66.4497 21.43V17.188H68.8437ZM77.9273 20.968C77.9273 21.192 77.9133 21.4253 77.8853 21.668H72.4673C72.5046 22.1533 72.6586 22.5267 72.9293 22.788C73.2093 23.04 73.55 23.166 73.9513 23.166C74.5486 23.166 74.964 22.914 75.1973 22.41H77.7453C77.6146 22.9233 77.3766 23.3853 77.0313 23.796C76.6953 24.2067 76.2706 24.5287 75.7573 24.762C75.244 24.9953 74.67 25.112 74.0353 25.112C73.27 25.112 72.5886 24.9487 71.9913 24.622C71.394 24.2953 70.9273 23.8287 70.5913 23.222C70.2553 22.6153 70.0873 21.906 70.0873 21.094C70.0873 20.282 70.2506 19.5727 70.5773 18.966C70.9133 18.3593 71.38 17.8927 71.9773 17.566C72.5746 17.2393 73.2606 17.076 74.0353 17.076C74.7913 17.076 75.4633 17.2347 76.0513 17.552C76.6393 17.8693 77.0966 18.322 77.4233 18.91C77.7593 19.498 77.9273 20.184 77.9273 20.968ZM75.4773 20.338C75.4773 19.9273 75.3373 19.6007 75.0573 19.358C74.7773 19.1153 74.4273 18.994 74.0073 18.994C73.606 18.994 73.2653 19.1107 72.9853 19.344C72.7146 19.5773 72.5466 19.9087 72.4813 20.338H75.4773Z"
      fill="white"
    />
    <path
      d="M93.082 17.188L90.968 25H88.322L87.09 19.932L85.816 25H83.184L81.056 17.188H83.45L84.556 22.774L85.872 17.188H88.406L89.736 22.746L90.828 17.188H93.082ZM95.199 16.376C94.779 16.376 94.4336 16.2547 94.163 16.012C93.9016 15.76 93.771 15.452 93.771 15.088C93.771 14.7147 93.9016 14.4067 94.163 14.164C94.4336 13.912 94.779 13.786 95.199 13.786C95.6096 13.786 95.9456 13.912 96.207 14.164C96.4776 14.4067 96.613 14.7147 96.613 15.088C96.613 15.452 96.4776 15.76 96.207 16.012C95.9456 16.2547 95.6096 16.376 95.199 16.376ZM96.389 17.188V25H93.995V17.188H96.389ZM102.478 22.97V25H101.26C100.392 25 99.7152 24.79 99.2299 24.37C98.7445 23.9407 98.5019 23.2453 98.5019 22.284V19.176H97.5499V17.188H98.5019V15.284H100.896V17.188H102.464V19.176H100.896V22.312C100.896 22.5453 100.952 22.7133 101.064 22.816C101.176 22.9187 101.363 22.97 101.624 22.97H102.478ZM108.613 17.104C109.509 17.104 110.228 17.4027 110.769 18C111.311 18.588 111.581 19.4 111.581 20.436V25H109.201V20.758C109.201 20.2353 109.066 19.8293 108.795 19.54C108.525 19.2507 108.161 19.106 107.703 19.106C107.246 19.106 106.882 19.2507 106.611 19.54C106.341 19.8293 106.205 20.2353 106.205 20.758V25H103.811V14.64H106.205V18.238C106.448 17.8927 106.779 17.6173 107.199 17.412C107.619 17.2067 108.091 17.104 108.613 17.104Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M125.357 19.3166C126.179 19.3166 126.816 18.6797 126.816 17.8583C126.816 17.0369 126.179 16.3999 125.357 16.3999C124.535 16.3999 123.898 17.0369 123.898 17.8583C123.898 18.6797 124.535 19.3166 125.357 19.3166Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M123.781 25.0161H126.934L126.263 19.8028C125.76 19.9537 124.955 19.9537 124.451 19.8028L123.781 25.0161Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M119.453 25.2384C121.583 25.2384 122.875 24.1166 122.875 22.3658C122.875 21.1421 122.137 20.3092 120.61 19.9693L119.118 19.6293C118.547 19.4934 118.246 19.3064 118.246 18.9664C118.246 18.6265 118.531 18.4056 119.168 18.4056C119.939 18.4056 120.745 18.7795 121.298 19.2894L122.556 17.4198C121.952 16.8758 120.711 16.3999 119.168 16.3999C117.172 16.3999 115.83 17.4027 115.83 19.1025C115.83 20.3432 116.518 21.244 117.977 21.584L119.47 21.941C120.208 22.1109 120.443 22.3319 120.443 22.6209C120.443 22.9948 120.141 23.1987 119.453 23.1987C118.648 23.1987 117.675 22.8588 116.887 22.1789L115.629 24.0486C116.216 24.5925 117.558 25.2384 119.453 25.2384Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M128.41 25.0685H130.925V20.4963C130.925 19.3745 131.462 18.6776 132.502 18.6776C133.492 18.6776 134.011 19.2555 134.011 20.4963V25.0685H136.527V19.8504C136.527 17.5728 135.404 16.4 133.475 16.4C132.368 16.4 131.529 16.8419 130.925 17.7087V17.1648C130.925 16.7909 130.707 16.5699 130.338 16.5699H128.41V25.0685Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M142.331 22.5869C141.274 22.5869 140.419 21.7201 140.419 20.6153C140.419 19.5104 141.274 18.6436 142.331 18.6436C143.471 18.6436 144.293 19.5104 144.293 20.6153C144.293 21.7201 143.471 22.5869 142.331 22.5869ZM144.796 16.5699C144.427 16.5699 144.209 16.7909 144.209 17.1648V17.6747C143.639 16.8079 142.733 16.4 141.66 16.4C139.63 16.4 137.869 18.2017 137.869 20.6152C137.869 23.0288 139.63 24.8305 141.66 24.8305C142.733 24.8305 143.605 24.4906 144.209 23.7257V24.2696C144.209 25.4254 143.454 26.2583 142.163 26.2583C141.006 26.2583 140.117 25.7653 139.563 25.2215L138.255 27.1931C139.094 27.958 140.486 28.3999 142.163 28.3999C144.679 28.3999 146.725 27.1251 146.725 23.9297V16.5699H144.796Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M153.115 22.9948C151.925 22.9948 151.002 22.0939 151.002 20.8192C151.002 19.5444 151.925 18.6435 153.115 18.6435C154.289 18.6435 155.178 19.5444 155.178 20.8192C155.178 22.0939 154.289 22.9948 153.115 22.9948ZM153.786 16.3999C152.646 16.3999 151.706 16.8078 151.086 17.6747V17.1647C151.086 16.7909 150.868 16.5699 150.499 16.5699H148.57V28.1959H151.086V24.1336C151.74 24.8984 152.646 25.2384 153.786 25.2384C155.967 25.2384 157.728 23.4367 157.728 20.8192C157.728 18.2016 155.967 16.3999 153.786 16.3999Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M163.413 22.9948C162.239 22.9948 161.351 22.0939 161.351 20.8192C161.351 19.5444 162.239 18.6435 163.413 18.6435C164.604 18.6435 165.527 19.5444 165.527 20.8192C165.527 22.0939 164.604 22.9948 163.413 22.9948ZM166.03 16.5699C165.661 16.5699 165.443 16.7909 165.443 17.1647V17.6747C164.822 16.8078 163.883 16.3999 162.743 16.3999C160.562 16.3999 158.801 18.2016 158.801 20.8192C158.801 23.4367 160.562 25.2384 162.743 25.2384C163.883 25.2384 164.822 24.8304 165.443 23.9636V25.0684H167.959V16.5699H166.03Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M173.057 25.2384C175.188 25.2384 176.479 24.1166 176.479 22.3658C176.479 21.1421 175.741 20.3092 174.215 19.9693L172.722 19.6293C172.152 19.4934 171.85 19.3064 171.85 18.9664C171.85 18.6265 172.135 18.4056 172.772 18.4056C173.544 18.4056 174.349 18.7795 174.902 19.2894L176.16 17.4198C175.556 16.8758 174.315 16.3999 172.772 16.3999C170.776 16.3999 169.435 17.4027 169.435 19.1025C169.435 20.3432 170.122 21.244 171.582 21.584L173.074 21.941C173.812 22.1109 174.047 22.3319 174.047 22.6209C174.047 22.9948 173.745 23.1987 173.057 23.1987C172.252 23.1987 171.28 22.8588 170.491 22.1789L169.233 24.0486C169.821 24.5925 171.162 25.2384 173.057 25.2384Z"
      fill="white"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M181.008 25.2384C183.138 25.2384 184.429 24.1166 184.429 22.3658C184.429 21.1421 183.691 20.3092 182.165 19.9693L180.672 19.6293C180.102 19.4934 179.8 19.3064 179.8 18.9664C179.8 18.6265 180.085 18.4056 180.723 18.4056C181.494 18.4056 182.299 18.7795 182.853 19.2894L184.11 17.4198C183.507 16.8758 182.265 16.3999 180.723 16.3999C178.727 16.3999 177.385 17.4027 177.385 19.1025C177.385 20.3432 178.073 21.244 179.532 21.584L181.024 21.941C181.762 22.1109 181.997 22.3319 181.997 22.6209C181.997 22.9948 181.695 23.1987 181.008 23.1987C180.203 23.1987 179.23 22.8588 178.441 22.1789L177.184 24.0486C177.771 24.5925 179.112 25.2384 181.008 25.2384Z"
      fill="white"
    />
  </svg>
);

// ─── Shared form components ───────────────────────────────────────────────────

const InputField = ({
  label,
  icon,
  type = "text",
  placeholder,
  value,
  onChange,
}) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-white/70 mb-1.5">
      {label}
    </label>
    <div className="flex items-center bg-white/10 border border-white/20 rounded-xl px-3 py-2.5 gap-2 focus-within:border-[#D4AF37]/60 transition-colors">
      <span className="text-white/40">{icon}</span>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`flex-1 text-sm text-white placeholder-white/30 outline-none bg-transparent ${type === "password" ? "tracking-widest" : ""}`}
      />
    </div>
  </div>
);

const ErrorBanner = ({ msg }) =>
  msg ? (
    <div className="mb-4 rounded-xl bg-red-900/30 border border-red-500/40 px-4 py-2.5 text-sm text-red-300">
      {msg}
    </div>
  ) : null;

const Divider = () => (
  <div className="flex items-center gap-3 mb-5">
    <div className="flex-1 h-px bg-white/15" />
    <span className="text-xs text-white/30 tracking-widest font-medium">
      OR
    </span>
    <div className="flex-1 h-px bg-white/15" />
  </div>
);

// ─── Brand panel (left side) ──────────────────────────────────────────────────

const brandFeatures = [
  { icon: Flag, text: "Tee times at 120+ premium courses across Asia" },
  { icon: Globe, text: "Curated golf getaways in Bali, Batam & Phuket" },
  { icon: Trophy, text: "Expert concierge from tee time to touchdown" },
  { icon: CheckCircle2, text: "Instant confirmation, flexible cancellation" },
];

function BrandPanel() {
  return (
    <div className="hidden lg:flex flex-col justify-between relative overflow-hidden bg-[#0B2421] p-12 min-h-screen">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200&q=80')",
        }}
      />
      {/* Gold top accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-[#D4AF37]" />

      <div className="relative z-10">
        {/* Logo */}
        <div className="mb-16">
          <img src={exquraWordmark} alt="Exqura" className="h-7 w-auto" />
          <p className="text-white/50 text-xs font-light tracking-[0.25em] uppercase mt-1.5">
            Golf & Experiences
          </p>
        </div>

        {/* Headline */}
        <div className="mb-12">
          <h2 className="text-white font-bold text-4xl xl:text-5xl leading-tight mb-4">
            Play the world's
            <br />
            <span className="text-[#D4AF37]">finest courses.</span>
          </h2>
          <p className="text-white/50 text-base leading-relaxed max-w-sm">
            From sunrise rounds in Bali to championship greens in Phuket — book
            elite tee times in seconds.
          </p>
        </div>

        {/* Features */}
        <div className="space-y-4">
          {brandFeatures.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-start gap-3">
              <div className="w-7 h-7 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center shrink-0 mt-0.5">
                <Icon className="w-3.5 h-3.5 text-[#D4AF37]" />
              </div>
              <p className="text-white/60 text-sm leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom quote */}
      <div className="relative z-10 border-l-2 border-[#D4AF37]/40 pl-4">
        <p className="text-white/40 text-sm italic leading-relaxed">
          "Golf is the closest game to the game we call life."
        </p>
        <p className="text-[#D4AF37]/60 text-xs mt-1">— Bobby Jones</p>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Login() {
  const navigate = useNavigate();

  const [view, setView] = useState("login");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(false);

  const clearError = () => setError("");

  const createFirestoreUser = async (user, name, provider) => {
    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      email: user.email,
      fullName: name,
      photoURL: user.photoURL ?? "",
      provider,
      role: "user",
      emailVerified: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  const getErrorCode = (error) => {
    if (typeof error === "object" && error !== null && "code" in error) {
      return error.code ?? "";
    }
    return "";
  };

  const handleSingpassSignIn = async () => {
    clearError();
    setLoading(true);
    try {
      const functions = getFunctions();
      const singpassInitiate = httpsCallable(functions, "singpassInitiate");
      const { data } = await singpassInitiate();
      window.location.href = data.authorizeUrl; // redirect browser to Singpass
    } catch (err) {
      console.error("Singpass login error:", err);
      setError("Could not start Singpass login. Try again.");
      setLoading(false);
    }
  };

  const handleSignIn = async () => {
    clearError();
    if (!loginEmail || !loginPassword)
      return setError("Enter your email and password.");
    setLoading(true);
    try {
      const result = await signInWithEmailAndPassword(
        auth,
        loginEmail,
        loginPassword,
      );
      if (!result.user.emailVerified) {
        setPendingUser(result.user);
        await sendEmailVerification(result.user);
        setView("verify");
        return;
      }
      navigate("/");
    } catch (err) {
      setError(friendlyError(getErrorCode(err)));
    } finally {
      setLoading(false);
    }
  };

  const getProviderForId = (providerId) => {
    if (providerId === GoogleAuthProvider.PROVIDER_ID)
      return new GoogleAuthProvider();
    if (providerId === FacebookAuthProvider.PROVIDER_ID)
      return new FacebookAuthProvider();
    return new OAuthProvider(providerId);
  };

  const upsertUserDoc = async (user, provider) => {
    await setDoc(
      doc(db, "users", user.uid),
      {
        uid: user.uid,
        email: user.email,
        fullName: user.displayName ?? "",
        photoURL: user.photoURL ?? "",
        provider,
        role: "user",
        emailVerified: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  };

  const handleSocialSignIn = async (provider, providerName) => {
    clearError();
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, provider);
      await upsertUserDoc(result.user, providerName);
      navigate("/");
    } catch (err) {
      const code = getErrorCode(err);

      if (code === "auth/account-exists-with-different-credential") {
        try {
          const pendingCred =
            provider === undefined
              ? null
              : providerName === "facebook"
                ? FacebookAuthProvider.credentialFromError(err)
                : GoogleAuthProvider.credentialFromError(err);

          const email = err.customData?.email;
          const methods = await fetchSignInMethodsForEmail(auth, email);

          if (methods.length === 0) {
            setError(
              "This email is already in use. Try a different sign-in method.",
            );
            return;
          }

          // Ask user to sign in with their original provider first
          const existingProviderId = methods[0]; // e.g. "google.com"
          const existingProvider = getProviderForId(existingProviderId);
          const existingResult = await signInWithPopup(auth, existingProvider);

          // Now link the new (Facebook/Google) credential to this account
          if (pendingCred) {
            await linkWithCredential(existingResult.user, pendingCred);
          }

          await upsertUserDoc(
            existingResult.user,
            existingProviderId.replace(".com", ""),
          );
          navigate("/");
        } catch (linkErr) {
          setError(friendlyError(getErrorCode(linkErr)));
        }
      } else {
        setError(friendlyError(code));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () =>
    handleSocialSignIn(new GoogleAuthProvider(), "google");

  const handleFacebookSignIn = () =>
    handleSocialSignIn(new FacebookAuthProvider(), "facebook");

  const handleSignUp = async () => {
    clearError();
    if (!fullName.trim()) return setError("Enter your full name.");
    if (!signupEmail) return setError("Enter your email.");
    if (signupPassword.length < 6)
      return setError("Password must be at least 6 characters.");
    if (signupPassword !== confirmPassword)
      return setError("Passwords don't match.");
    setLoading(true);
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        signupEmail,
        signupPassword,
      );
      await sendEmailVerification(result.user);
      setPendingUser(result.user);
      setView("verify");
    } catch (err) {
      setError(friendlyError(getErrorCode(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerified = async () => {
    clearError();
    setChecking(true);
    try {
      if (!pendingUser) {
        setError("No pending user found. Please sign up first.");
        return;
      }
      await pendingUser.reload();
      if (pendingUser.emailVerified) {
        await createFirestoreUser(pendingUser, fullName, "email");
        navigate("/");
      } else {
        setError("Email not verified yet. Click the link in your inbox first.");
      }
    } catch {
      setError("Something went wrong. Try again.");
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    clearError();
    if (resendCooldown) return;
    try {
      if (!pendingUser) {
        setError("No pending user to resend verification for.");
        return;
      }
      await sendEmailVerification(pendingUser);
      setResendCooldown(true);
      setTimeout(() => setResendCooldown(false), 60_000);
    } catch {
      setError("Could not resend. Try again in a moment.");
    }
  };

  const openForgot = () => {
    clearError();
    setResetEmail(loginEmail); // prefill if they already typed it
    setResetSent(false);
    setView("forgot");
  };

  const handleSendReset = async () => {
    clearError();
    const email = resetEmail.trim();
    if (!email) return setError("Enter your email.");
    if (resetCooldown) return;

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      markResetSent();
    } catch (err) {
      const code = getErrorCode(err);
      if (code === "auth/user-not-found") {
        // Same success screen either way, so the form can't be used
        // to check which emails have accounts.
        markResetSent();
      } else {
        setError(friendlyError(code));
      }
    } finally {
      setLoading(false);
    }
  };

  const markResetSent = () => {
    setResetSent(true);
    setResetCooldown(true);
    setTimeout(() => setResetCooldown(false), 60_000);
  };

  const emailSvg = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M2.667 2.667h10.666A1.333 1.333 0 0 1 14.667 4v8a1.333 1.333 0 0 1-1.334 1.333H2.667A1.333 1.333 0 0 1 1.333 12V4a1.333 1.333 0 0 1 1.334-1.333Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m14.667 4-6.667 4.667L1.333 4"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
  const lockSvg = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect
        x="2.667"
        y="7.333"
        width="10.666"
        height="7.334"
        rx="1.333"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M5.333 7.333V4.667a2.667 2.667 0 0 1 5.334 0v2.666"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
  const userSvg = (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle
        cx="8"
        cy="5"
        r="3"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 14c0-3.314 2.686-5 6-5s6 1.686 6 5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: Brand panel */}
      <BrandPanel />

      {/* Right: Auth form */}
      <div className="flex flex-col bg-[#091E1B] min-h-screen">
        {/* Mobile logo */}
        <div className="lg:hidden px-8 pt-8 pb-2">
          <img src={exquraWordmark} alt="Exqura" className="h-6 w-auto" />
          <p className="text-white/40 text-xs tracking-widest uppercase mt-1">
            Golf & Experiences
          </p>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 sm:px-10 py-10">
          <div className="w-full max-w-[420px]">
            {/* ── LOGIN ────────────────────────────────────────────── */}
            {view === "login" && (
              <>
                <div className="mb-8">
                  <h1 className="text-white font-bold text-3xl mb-1">
                    Welcome back
                  </h1>
                  <p className="text-white/50 text-sm">
                    Sign in to access your golf bookings
                  </p>
                </div>
                {/* <button
                  type="button"
                  onClick={handleSingpassSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center bg-[#D93841]  rounded-xl overflow-hidden hover:bg-[#e60000] transition-colors disabled:opacity-50 mb-4 py-1"
                >
                  <SignpassButton  />
                </button> */}

                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-white/10 border border-white/20 rounded-xl py-3 text-sm font-medium text-white hover:bg-white/15 hover:border-white/30 transition-colors mb-5 disabled:opacity-50"
                >
                  <GoogleIcon />
                  Continue with Google
                </button>

                <button
                  onClick={handleFacebookSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 rounded-lg border border-gray-300 bg-[#1877F2] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#166FE5] mb-10 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="white"
                    aria-hidden="true"
                  >
                    <path d="M22 12.06C22 6.505 17.523 2 12 2S2 6.505 2 12.06c0 5.02 3.657 9.184 8.438 9.94v-7.03H7.898v-2.91h2.54V9.845c0-2.507 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562v1.877h2.773l-.443 2.91h-2.33V22c4.78-.756 8.437-4.92 8.437-9.94Z" />
                  </svg>
                  Continue with Facebook
                </button>

                <Divider />

                <ErrorBanner msg={error} />

                <InputField
                  label="Email"
                  icon={emailSvg}
                  type="email"
                  placeholder="you@example.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                />
                <InputField
                  label="Password"
                  icon={lockSvg}
                  type="password"
                  placeholder="••••••••"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                />

                <div className="flex justify-end mb-5">
                  <button
                    type="button"
                    onClick={openForgot}
                    className="text-xs text-[#D4AF37]/70 hover:text-[#D4AF37] transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  onClick={handleSignIn}
                  disabled={loading}
                  className="w-full bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] text-sm font-bold rounded-xl py-3.5 transition-colors mb-5 disabled:opacity-50"
                >
                  {loading ? "Signing in…" : "Sign In"}
                </button>

                <p className="text-center text-sm text-white/40">
                  No account?{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setView("signup");
                      clearError();
                    }}
                    className="text-[#D4AF37] font-semibold hover:underline"
                  >
                    Create one
                  </button>
                </p>
              </>
            )}

            {/* ── SIGNUP ───────────────────────────────────────────── */}
            {view === "signup" && (
              <>
                <div className="mb-8">
                  <h1 className="text-white font-bold text-3xl mb-1">
                    Create account
                  </h1>
                  <p className="text-white/50 text-sm">
                    Join Exqura and start booking premium golf
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-white/10 border border-white/20 rounded-xl py-3 text-sm font-medium text-white hover:bg-white/15 hover:border-white/30 transition-colors mb-5 disabled:opacity-50"
                >
                  <GoogleIcon />
                  Continue with Google
                </button>

                <button
                  onClick={handleFacebookSignIn}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 rounded-lg border border-gray-300 bg-[#1877F2] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#166FE5] disabled:opacity-50 mb-10 disabled:cursor-not-allowed"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="white"
                    aria-hidden="true"
                  >
                    <path d="M22 12.06C22 6.505 17.523 2 12 2S2 6.505 2 12.06c0 5.02 3.657 9.184 8.438 9.94v-7.03H7.898v-2.91h2.54V9.845c0-2.507 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562v1.877h2.773l-.443 2.91h-2.33V22c4.78-.756 8.437-4.92 8.437-9.94Z" />
                  </svg>
                  Continue with Facebook
                </button>

                <Divider />
                <ErrorBanner msg={error} />

                <InputField
                  label="Full Name"
                  icon={userSvg}
                  placeholder="Alex Morgan"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
                <InputField
                  label="Email"
                  icon={emailSvg}
                  type="email"
                  placeholder="you@example.com"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                />
                <InputField
                  label="Password"
                  icon={lockSvg}
                  type="password"
                  placeholder="Min. 6 characters"
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                />
                <InputField
                  label="Confirm Password"
                  icon={lockSvg}
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />

                <button
                  onClick={handleSignUp}
                  disabled={loading}
                  className="w-full bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] text-sm font-bold rounded-xl py-3.5 transition-colors mb-5 disabled:opacity-50"
                >
                  {loading ? "Creating account…" : "Create Account"}
                </button>

                <p className="text-center text-sm text-white/40">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setView("login");
                      clearError();
                    }}
                    className="text-[#D4AF37] font-semibold hover:underline"
                  >
                    Sign in
                  </button>
                </p>
              </>
            )}

            {/* ── VERIFY EMAIL ─────────────────────────────────────── */}
            {view === "verify" && (
              <>
                <div className="flex justify-center mb-6">
                  <MailCheckIcon />
                </div>

                <div className="mb-8 text-center">
                  <h1 className="text-white font-bold text-3xl mb-2">
                    Check your email
                  </h1>
                  <p className="text-white/50 text-sm mb-1">
                    We sent a verification link to
                  </p>
                  <p className="text-[#D4AF37] font-semibold text-sm">
                    {pendingUser?.email}
                  </p>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 mb-6 text-sm text-white/50 text-center leading-relaxed">
                  Click the link in the email, then come back and tap the button
                  below.
                </div>

                <ErrorBanner msg={error} />

                <button
                  onClick={handleCheckVerified}
                  disabled={checking}
                  className="w-full bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] text-sm font-bold rounded-xl py-3.5 transition-colors mb-5 disabled:opacity-50"
                >
                  {checking ? "Checking…" : "I've verified my email"}
                </button>

                <div className="flex flex-col items-center gap-2">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCooldown}
                    className="text-sm text-white/40 hover:text-white/70 transition-colors disabled:opacity-40"
                  >
                    {resendCooldown
                      ? "Link sent — check your inbox"
                      : "Resend verification email"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setView("signup");
                      clearError();
                      setPendingUser(null);
                    }}
                    className="text-sm text-white/40 hover:text-white/70 transition-colors"
                  >
                    ← Use a different email
                  </button>
                </div>
              </>
            )}

            {/* ── FORGOT PASSWORD ──────────────────────────────────── */}
            {view === "forgot" && (
              <>
                {resetSent ? (
                  <>
                    <div className="flex justify-center mb-6">
                      <MailCheckIcon />
                    </div>
                    <div className="mb-8 text-center">
                      <h1 className="text-white font-bold text-3xl mb-2">
                        Check your email
                      </h1>
                      <p className="text-white/50 text-sm mb-1">
                        If an account exists for
                      </p>
                      <p className="text-[#D4AF37] font-semibold text-sm mb-1">
                        {resetEmail.trim()}
                      </p>
                      <p className="text-white/50 text-sm">
                        we've sent a link to reset your password.
                      </p>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 mb-6 text-sm text-white/50 text-center leading-relaxed">
                      Can't find it? Check your spam folder.
                    </div>

                    <ErrorBanner msg={error} />

                    <div className="flex flex-col items-center gap-2">
                      <button
                        type="button"
                        onClick={handleSendReset}
                        disabled={resetCooldown || loading}
                        className="text-sm text-white/40 hover:text-white/70 transition-colors disabled:opacity-40"
                      >
                        {resetCooldown
                          ? "Link sent — try again in a minute"
                          : "Resend link"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setView("login");
                          setResetSent(false);
                          clearError();
                        }}
                        className="text-sm text-[#D4AF37] font-semibold hover:underline"
                      >
                        Back to sign in
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mb-8">
                      <h1 className="text-white font-bold text-3xl mb-1">
                        Reset password
                      </h1>
                      <p className="text-white/50 text-sm">
                        Enter your email and we'll send you a link to reset your
                        password
                      </p>
                    </div>

                    <ErrorBanner msg={error} />

                    <InputField
                      label="Email"
                      icon={emailSvg}
                      type="email"
                      placeholder="you@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                    />

                    <button
                      onClick={handleSendReset}
                      disabled={loading}
                      className="w-full bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] text-sm font-bold rounded-xl py-3.5 transition-colors mb-5 disabled:opacity-50"
                    >
                      {loading ? "Sending…" : "Send Reset Link"}
                    </button>

                    <p className="text-center text-sm text-white/40">
                      Remembered it?{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setView("login");
                          clearError();
                        }}
                        className="text-[#D4AF37] font-semibold hover:underline"
                      >
                        Sign in
                      </button>
                    </p>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/20 text-xs pb-6">
          © {new Date().getFullYear()} Exqura Golf & Experiences. All rights
          reserved.
        </p>
      </div>
    </div>
  );
}

// ─── Friendly Firebase errors ─────────────────────────────────────────────────

function friendlyError(code) {
  const map = {
    "auth/email-already-in-use":
      "This email is already registered. Sign in instead.",
    "auth/invalid-email": "Enter a valid email address.",
    "auth/weak-password": "Password must be at least 6 characters.",
    "auth/user-not-found": "No account with this email.",
    "auth/wrong-password": "Incorrect password.",
    "auth/invalid-credential": "Incorrect email or password.",
    "auth/too-many-requests": "Too many attempts. Try again later.",
    "auth/popup-closed-by-user": "Google sign-in was cancelled.",
    "auth/network-request-failed": "Network error. Check your connection.",
    "auth/missing-email": "Enter your email.",
  };
  return map[code] ?? "Something went wrong. Please try again.";
}
